import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import { ALLOWED_EMAIL_DOMAIN, isAllowedEmail } from '../config/access';
import type { UserInfo, UserRole } from '../types';

const USERS_COLLECTION = 'users';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Raised when a signed-in account isn't allowed in: not a company email, or
 * the email isn't verified yet. The widget shows `message` and (except while
 * waiting on verification) signs the account back out.
 */
export class AccessDeniedError extends Error {
  constructor(
    message: string,
    readonly reason: 'wrong-domain' | 'unverified',
  ) {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

/**
 * Check the account is allowed in, then load its profile — creating it on
 * first sign-in.
 *
 * Only verified @mattersoho.co.uk accounts get in (plus the legacy
 * @sirendesign.co.uk — see config/access.ts). The Firestore rules
 * enforce the same (this check is just for a clear message). Verification
 * matters: without it anyone could sign up as an address they don't own.
 */
export const loadOrCreateUserProfile = async (
  userId: string,
  email: string,
  emailVerified: boolean,
  name?: string,
): Promise<UserProfile> => {
  if (!isAllowedEmail(email)) {
    throw new AccessDeniedError(
      `The feedback tool is for Matter staff. Sign in with your @${ALLOWED_EMAIL_DOMAIN} email.`,
      'wrong-domain',
    );
  }
  if (!emailVerified) {
    throw new AccessDeniedError(
      'Please verify your email address — we’ve sent you a link. Then sign in again.',
      'unverified',
    );
  }

  const db = getFirebaseDb();
  const userRef = doc(db, USERS_COLLECTION, userId);
  const existing = await getDoc(userRef);
  if (existing.exists()) return existing.data() as UserProfile;

  const now = new Date().toISOString();
  const profile: UserProfile = {
    id: userId,
    name: name || email.split('@')[0],
    email,
    // Everyone starts as a member; admins promote from the Team screen.
    role: 'default',
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(userRef, profile);
  return profile;
};

/** Everyone with a profile — the assignee picker and the Team screen. */
export const listTeamMembers = async (): Promise<UserInfo[]> => {
  const snap = await getDocs(collection(getFirebaseDb(), USERS_COLLECTION));
  return snap.docs
    .map((d) => d.data() as UserProfile)
    .map(({ id, name, email, role, avatar }) => ({ id, name, email, role, avatar }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/** Change a user's role (admins only — enforced by the rules). */
export const updateUserRole = async (userId: string, newRole: UserRole): Promise<void> => {
  await updateDoc(doc(getFirebaseDb(), USERS_COLLECTION, userId), {
    role: newRole,
    updatedAt: new Date().toISOString(),
  });
};

/** Change the signed-in user's display name. */
export const updateUserName = async (userId: string, newName: string): Promise<void> => {
  await updateDoc(doc(getFirebaseDb(), USERS_COLLECTION, userId), {
    name: newName,
    updatedAt: new Date().toISOString(),
  });
};

const isStaff = (user: UserInfo) => user.role === 'admin' || user.role === 'developer';

/** The creator, developers and admins can delete a ticket. */
export const canDeleteFeedback = (currentUser: UserInfo, feedbackCreatorId: string): boolean =>
  currentUser.id === feedbackCreatorId || isStaff(currentUser);

/** The creator, developers and admins can edit a ticket's content. */
export const canEditFeedback = (currentUser: UserInfo, feedbackCreatorId: string): boolean =>
  currentUser.id === feedbackCreatorId || isStaff(currentUser);

/**
 * Status, assignee and due date. Everyone using the tool is a colleague, so
 * anyone signed in can move a ticket along.
 */
export const canManageWorkflow = (currentUser: UserInfo | null): boolean => !!currentUser;

export const isAdmin = (currentUser: UserInfo | null): boolean =>
  currentUser?.role === 'admin';
